import React from "react";
import { getCutoutImage } from "../../utils/getCutoutImage";
import { cssVars } from "../../theme";
import { BagIcon, CloseIcon, WhatsAppIcon } from "./ShopIcons";
import "../../styles/hlcShopSystem.css";
import { useModalDialog } from "../../hooks/useModalDialog";

/**
 * Shared Quick View — a compact product preview (not a second page).
 * Used by both ShopScreen.jsx and DiscountedProducts.js so "Eye → Quick
 * View → View Full Product" feels like one continuous experience.
 *
 * Primary action = Add to Cart, secondary = View Full Product, tertiary
 * = a small WhatsApp text link (no longer a large green button).
 */
export default function QuickViewModal({
  product,
  open,
  onClose,
  cartItem,
  onAddToCart,
  onIncrement,
  onDecrement,
  onViewFull,
  whatsappNumber = "923337244858",
}) {
  const { dialogRef } = useModalDialog(open && Boolean(product), onClose);
  if (!product || !open) return null;

  const qty = cartItem?.quantity || 1;
  const hasDiscount = Boolean(product.discountedPrice);
  const image = getCutoutImage(product);
  // Real-data-only badge, same priority as the shared ProductCard.
  const badge = product.discount || (product.topSelling && "Best Seller") || (product.featured && "Popular") || null;

  return (
    <div className={`hlc-qv-modal ${open ? "active" : ""}`} style={cssVars}>
      <div className="hlc-qv-overlay" onClick={onClose}></div>
      <div className="hlc-qv-box" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="hlc-qv-title" tabIndex={-1}>
        <button className="hlc-qv-close" onClick={onClose} type="button" aria-label="Close quick view">
          <CloseIcon />
        </button>

        <div className="hlc-qv-grid">
          <div className="hlc-qv-media">
            {badge && (
              <span className="hlc-qv-media-badge">{badge}</span>
            )}
            <img src={image} alt={product.name} />
          </div>

          <div className="hlc-qv-info">
            {product.category && <p className="hlc-qv-eyebrow">{product.category}</p>}
            <h2 id="hlc-qv-title">{product.name}</h2>
            {product.description && <p className="hlc-qv-desc">{product.description}</p>}

            {(product.subcategory || product.wattColourModel || product.stock) && (
              <ul className="hlc-qv-specs">
                {product.subcategory && <li><b>Subcategory:</b> {product.subcategory}</li>}
                {product.wattColourModel && (
                  <li>
                    <b>Watt / Model:</b>{" "}
                    {Array.isArray(product.wattColourModel) ? product.wattColourModel.join(", ") : product.wattColourModel}
                  </li>
                )}
                {product.stock && <li><b>Stock:</b> {product.stock}</li>}
              </ul>
            )}

            <div className="hlc-qv-price-row">
              <span className="hlc-qv-price">Rs {product.discountedPrice || product.price}</span>
              {hasDiscount && <span className="hlc-qv-price-old">Rs {product.price}</span>}
              {hasDiscount && product.discount && <span className="hlc-qv-discount-tag">{product.discount}</span>}
            </div>

            <div className="hlc-qv-actions">
              <div className="hlc-qv-primary-row">
                {!cartItem ? (
                  <button type="button" className="hlc-qv-addcart" onClick={() => onAddToCart(product)}>
                    <BagIcon /> Add to Cart
                  </button>
                ) : (
                  <>
                    <div className="hlc-qv-counter">
                      <button type="button" aria-label={`Decrease quantity of ${product.name}`} onClick={() => onDecrement(product.id)}>-</button>
                      <span>{qty}</span>
                      <button type="button" aria-label={`Increase quantity of ${product.name}`} onClick={() => onIncrement(product.id)}>+</button>
                    </div>
                  </>
                )}
              </div>

              <button type="button" className="hlc-qv-viewfull" onClick={() => onViewFull(product.id)}>
                View Full Product →
              </button>

              <a
                className="hlc-qv-whatsapp"
                href={`https://wa.me/${whatsappNumber}?text=I%20am%20interested%20in%20${encodeURIComponent(product.name)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <WhatsAppIcon /> WhatsApp Inquiry
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
